import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AdminSavedPost, AdminSavedPostList } from "@ternary/contracts"
import {
  Button,
  type Column,
  Dialog,
  EmptyState,
  Field,
  Input,
  Muted,
  SearchInput,
  Select,
  Table,
  useToast,
} from "@ternary/ui"
import { type ReactElement, useState } from "react"
import { Navigate } from "react-router-dom"
import {
  AdminApiError,
  loadAdminDiscord,
  loadSavedChannels,
  loadSavedPost,
  loadSavedPosts,
  type SavedPostFilters,
  saveSavedChannels,
} from "../api.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { SavedChannelsCard } from "../components/saved-channels-card.js"

const DATE = new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" })

/** A post's text as the list shows it: deleted, withheld by Discord, or the text. */
export function postText(post: Pick<AdminSavedPost, "content" | "contentAvailable" | "deletedAt">) {
  if (post.deletedAt) return "Deleted in Discord"
  if (!post.contentAvailable) return "Text not available yet"
  return post.content || "(no text)"
}

function Files({ post }: { post: AdminSavedPost }) {
  if (post.attachments.length === 0) return null
  return (
    <span className="admin-saved-files">
      {post.attachments.map((file) =>
        file.downloadUrl ? (
          <a download href={file.downloadUrl} key={file.id}>
            {file.filename}
          </a>
        ) : (
          <Muted key={file.id}>{file.filename}</Muted>
        ),
      )}
    </span>
  )
}

/**
 * The saved posts, newest first or by channel, filtered by channel: each one's channel, poster,
 * text (or why there is none) and files; View opens it with its thread replies. It only shows.
 */
export function SavedPostList({
  loading = false,
  onView,
  posts,
}: {
  loading?: boolean
  onView(post: AdminSavedPost): void
  posts: readonly AdminSavedPost[]
}): ReactElement {
  const columns: Column<AdminSavedPost>[] = [
    { cell: (post) => `#${post.channelName}`, header: "Channel", key: "channel" },
    {
      cell: (post) => (
        <div className="admin-poll-question">
          {post.threadName ? <strong>{post.threadName}</strong> : null}
          <span className={post.deletedAt || !post.contentAvailable ? "admin-note" : undefined}>
            {postText(post)}
          </span>
          <Files post={post} />
        </div>
      ),
      header: "Post",
      key: "post",
    },
    { cell: (post) => post.authorUsername, header: "Poster", key: "poster", priority: 2 },
    {
      cell: (post) => DATE.format(new Date(post.postedAt)),
      header: "Posted",
      key: "posted",
      priority: 2,
    },
    {
      align: "end",
      cell: (post) => (
        <Button onClick={() => onView(post)} size="sm" variant="secondary">
          View
        </Button>
      ),
      header: "Actions",
      key: "actions",
      skeleton: "action",
    },
  ]
  return (
    <Table
      caption="Saved posts"
      captionHidden
      columns={columns}
      loading={loading}
      loadingLabel="Loading saved posts…"
      rowKey={(post) => post.id}
      rows={[...posts]}
    />
  )
}

function PostDialog({ onClose, post }: { onClose(): void; post: AdminSavedPost | null }) {
  const detail = useQuery({
    enabled: post !== null,
    queryFn: ({ signal }) => loadSavedPost(post?.id ?? "", { signal }),
    queryKey: ["saved-post", post?.id],
  })
  return (
    <Dialog
      onClose={onClose}
      open={post !== null}
      size="lg"
      title={post ? `Post in #${post.channelName}` : "Post"}
    >
      {post ? (
        <div className="admin-stack">
          <p>
            <strong>{post.authorUsername}</strong>{" "}
            <Muted>
              {DATE.format(new Date(post.postedAt))} · Discord ID {post.authorId}
            </Muted>
          </p>
          <p>{postText(post)}</p>
          <Files post={post} />
          {detail.isError ? (
            <LoadError
              detail={errorDetail(detail.error)}
              message="We couldn't load this post's replies"
              onRetry={() => void detail.refetch()}
              retrying={detail.isFetching}
            />
          ) : null}
          {detail.data && detail.data.replies.length > 0 ? (
            <section className="admin-stack admin-stack-tight" aria-label="Thread replies">
              <h3 className="admin-heading">
                {detail.data.replies.length}{" "}
                {detail.data.replies.length === 1 ? "reply" : "replies"}
              </h3>
              {detail.data.replies.map((reply) => (
                <div className="admin-saved-reply" key={reply.id}>
                  <p>
                    <strong>{reply.authorUsername}</strong>{" "}
                    <Muted>{DATE.format(new Date(reply.postedAt))}</Muted>
                  </p>
                  <p>{postText(reply)}</p>
                  <Files post={reply} />
                </div>
              ))}
            </section>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  )
}

/**
 * X3: the Saved posts tab of a deployment that offers community features but not saved posts yet.
 * It only says what is coming: no control, no saved-posts call, no job.
 */
export function SavedPostsComingSoon(): ReactElement {
  return (
    <EmptyState
      description="Coming soon: keep a copy of every post in the channels you pick, attachments included, for your team to browse and export."
      title="Saved posts"
    />
  )
}

/**
 * Saved posts (Connections → Discord → Saved posts), while the deployment offers them: the saved
 * channels card, then the posts saved from them. Any admin reads; owners and admins choose.
 */
export function SavedPostsPanel(): ReactElement {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [filters, setFilters] = useState<SavedPostFilters>({ sort: "time" })
  const [viewing, setViewing] = useState<AdminSavedPost | null>(null)
  const channels = useQuery({ queryFn: loadSavedChannels, queryKey: ["saved-channels"] })
  const posts = useInfiniteQuery({
    getNextPageParam: (page: AdminSavedPostList) => page.nextCursor,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => loadSavedPosts(filters, pageParam, { signal }),
    queryKey: ["saved-posts", filters],
  })
  const save = useMutation({
    mutationFn: saveSavedChannels,
    onError: (error) =>
      toast({
        message:
          error instanceof AdminApiError
            ? error.message
            : "The saved channels could not reach the server. Check your connection, then try again.",
        tone: "error",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["saved-channels"] })
      toast({ message: "Saved channels changed.", tone: "success" })
    },
  })
  const rows = (posts.data?.pages ?? []).flatMap(({ data }) => data)

  if (channels.isError) {
    return (
      <LoadError
        detail={errorDetail(channels.error)}
        message="We couldn't load saved posts"
        onRetry={() => void channels.refetch()}
        retrying={channels.isFetching}
      />
    )
  }
  return (
    <div className="admin-stack">
      {channels.data ? (
        <SavedChannelsCard
          busy={save.isPending}
          key={JSON.stringify(channels.data.channels)}
          onSave={(chosen) => save.mutate(chosen)}
          view={channels.data}
        />
      ) : (
        <p className="admin-note" role="status">
          Loading saved channels…
        </p>
      )}
      <div className="admin-row">
        <Field label="Channel">
          <Select
            onChange={(event) =>
              setFilters({ ...filters, channelId: event.target.value || undefined })
            }
            value={filters.channelId ?? ""}
          >
            <option value="">All channels</option>
            {(channels.data?.channels ?? []).map((channel) => (
              <option key={channel.channelId} value={channel.channelId}>
                #{channel.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Sort">
          <Select
            onChange={(event) =>
              setFilters({
                ...filters,
                sort: event.target.value === "channel" ? "channel" : "time",
              })
            }
            value={filters.sort}
          >
            <option value="time">Newest first</option>
            <option value="channel">By channel</option>
          </Select>
        </Field>
        <Field label="From">
          <Input
            onChange={(event) => setFilters({ ...filters, from: event.target.value || undefined })}
            type="date"
            value={filters.from ?? ""}
          />
        </Field>
        <Field label="To">
          <Input
            onChange={(event) => setFilters({ ...filters, to: event.target.value || undefined })}
            type="date"
            value={filters.to ?? ""}
          />
        </Field>
        <SearchInput
          label="Search posts"
          maxLength={100}
          onSearch={(search) => setFilters({ ...filters, search: search.trim() || undefined })}
          value={filters.search ?? ""}
        />
      </div>
      {posts.isError ? (
        <LoadError
          detail={errorDetail(posts.error)}
          message="We couldn't load saved posts"
          onRetry={() => void posts.refetch()}
          retrying={posts.isFetching}
        />
      ) : !posts.isPending && rows.length === 0 ? (
        <EmptyState
          description="Posts appear here a couple of minutes after they are made in a saved channel."
          title="No saved posts yet"
        />
      ) : (
        <SavedPostList loading={posts.isPending} onView={setViewing} posts={rows} />
      )}
      {posts.hasNextPage ? (
        <Button
          loading={posts.isFetchingNextPage}
          onClick={() => void posts.fetchNextPage()}
          variant="secondary"
        >
          Show older posts
        </Button>
      ) : null}
      <PostDialog onClose={() => setViewing(null)} post={viewing} />
    </div>
  )
}

/**
 * /dashboard/discord/saved-posts: the Discord page's Saved posts tab while the deployment offers
 * them; with the switch off it goes home, as any unknown address does.
 */
export function AdminSavedPostsPage(): ReactElement | null {
  const query = useQuery({ queryFn: loadAdminDiscord, queryKey: ["admin-discord"] })
  if (query.isPending) return null
  // Only a loaded answer without saved posts sends the link home; a failed load says so.
  if (query.isError) {
    return (
      <LoadError
        detail={errorDetail(query.error)}
        message="We couldn't load your Discord settings"
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    )
  }
  return (
    <Navigate
      replace
      to={query.data.community?.savedPosts ? "/dashboard/discord?tab=saved-posts" : "/"}
    />
  )
}
